/*
 * Carries the "ship to the recipient, not yourself" warning from the cart into
 * Shopify checkout.
 *
 * Checkout itself can't be themed on this plan, and cart attributes (where the
 * gift details live) are admin-only — they never render for the buyer. A line
 * item property does render, under the product title in the order summary, so
 * the reminder is attached to the first cart line just before checkout opens.
 *
 * That summary is collapsed behind "Show order summary" on mobile, so the first
 * Checkout click also surfaces a confirm panel in the drawer. The property is
 * the backstop; the panel is what most buyers actually read.
 */
(function () {
  const GIFT_EMAIL_ATTRIBUTE = 'Gift recipient email';
  const GIFT_ATTRIBUTES = [GIFT_EMAIL_ATTRIBUTE, 'Gift note', 'Gift from'];

  // Re-entry guard: proceed() re-fires submit with the original submitter, and
  // that second pass has to reach the browser untouched.
  let submitting = false;
  // Which recipient the buyer has already acknowledged. Keyed by value rather
  // than a boolean so editing the details re-arms the confirm step.
  let confirmedFor = null;

  const strings = () => (window.theme && theme.giftStrings) || null;

  document.addEventListener('submit', onSubmit);
  document.addEventListener('click', onClick);

  function onSubmit(event) {
    const submitter = event.submitter;
    if (!submitter || submitter.name !== 'checkout') return;
    if (submitting) return;
    if (!strings()) return;

    event.preventDefault();
    run(event.target, submitter);
  }

  // The confirm panel's continue button is a plain button, so it replays the
  // checkout submit itself once the buyer has acknowledged the recipient.
  // Its edit button just hands off to the gift drawer, so the panel steps aside.
  function onClick(event) {
    const edit = event.target.closest('[data-gift-note-drawer-toggle]');
    if (edit) {
      const openPanel = edit.closest('[data-gift-confirm]');
      if (openPanel) hideConfirm(openPanel);
      return;
    }

    const button = event.target.closest('[data-gift-confirm-continue]');
    if (!button) return;

    const panel = button.closest('[data-gift-confirm]');
    const form = button.closest('form');
    if (!panel || !form) return;

    confirmedFor = panel.dataset.giftConfirmRecipient || '';
    hideConfirm(panel);

    const submitter = form.querySelector('[name="checkout"]');
    if (submitter) run(form, submitter);
  }

  async function run(form, submitter) {
    setBusy(submitter, true);

    let cart;
    try {
      cart = await getCart();
    } catch (e) {
      // Never let this feature stand between the buyer and checkout.
      console.error(e);
      return proceed(form, submitter);
    }

    const recipient = giftRecipient(cart);
    const panel = form.querySelector('[data-gift-confirm]');

    if (recipient && panel && confirmedFor !== recipient) {
      setBusy(submitter, false);
      showConfirm(panel, recipient);
      return;
    }

    try {
      await reconcile(cart, recipient);
    } catch (e) {
      console.error(e);
    }

    proceed(form, submitter);
  }

  // The saved recipient email, or null when this isn't a gift order. Falls back
  // to the generic wording when the buyer filled in a message but no email.
  function giftRecipient(cart) {
    const attributes = cart.attributes || {};
    const hasGift = GIFT_ATTRIBUTES.some((key) => (attributes[key] || '').trim());
    if (!hasGift) return null;
    return (attributes[GIFT_EMAIL_ATTRIBUTE] || '').trim() || strings().recipientFallback;
  }

  function propertyValue(recipient) {
    return strings().propertyValue.replace('[recipient]', recipient);
  }

  /*
   * Bring the cart to the intended state: the reminder on the first taggable
   * line, nowhere else. One /cart/change.js per line that's wrong — normally
   * zero (nothing changed since the last checkout click) or one.
   *
   * Each change rewrites the line's key, so the cart is re-read from every
   * response instead of holding stale keys across the loop.
   */
  async function reconcile(cart, recipient) {
    const key = strings().propertyName;
    const wanted = recipient ? propertyValue(recipient) : '';
    let current = cart;

    // Bounded so a value Shopify refuses to store can't spin forever.
    const maxPasses = (cart.items || []).length + 2;

    for (let pass = 0; pass < maxPasses; pass++) {
      const target = pickTarget(current.items);
      const wrong = current.items.find((item) => {
        const has = (item.properties && item.properties[key]) || '';
        const want = item === target ? wanted : '';
        return has !== want;
      });
      if (!wrong) return;

      // change.js replaces the whole property set, so existing properties are
      // carried over. Clearing writes an empty value rather than dropping the
      // key: an empty `properties` object reads as "no change" to Shopify and
      // the stale reminder would survive. Blank values aren't rendered.
      const properties = Object.assign({}, wrong.properties);
      properties[key] = wrong === target ? wanted : '';
      current = await changeProperties(wrong.key, properties);
    }
  }

  // The note is about the shipping address, so it belongs on something that
  // ships — a donation or gift card sitting first in the cart would read oddly.
  function pickTarget(items) {
    const taggable = items.filter(isTaggable);
    return taggable.find((item) => item.requires_shipping) || taggable[0] || null;
  }

  // Gift-card lines carry __shopify_* properties that Shopify owns; rewriting
  // their property set risks breaking recipient delivery, so tag the next line
  // along instead.
  function isTaggable(item) {
    return !Object.keys(item.properties || {}).some((name) => name.indexOf('__shopify') === 0);
  }

  function getCart() {
    return fetch(`${theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(handle);
  }

  function changeProperties(id, properties) {
    return fetch(`${theme.routes.cart_change_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({ id, properties })
    }).then(handle);
  }

  function handle(response) {
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  function showConfirm(panel, recipient) {
    panel.dataset.giftConfirmRecipient = recipient;
    panel.hidden = false;
    footer(panel).classList.add('is-confirming');
    const continueButton = panel.querySelector('[data-gift-confirm-continue]');
    if (continueButton) continueButton.focus();
  }

  function hideConfirm(panel) {
    panel.hidden = true;
    footer(panel).classList.remove('is-confirming');
  }

  function footer(panel) {
    return panel.closest('.mini-cart__footer') || panel.parentElement;
  }

  function setBusy(submitter, busy) {
    if (!submitter) return;
    submitter.setAttribute('aria-busy', String(busy));
    submitter.disabled = busy;
  }

  function proceed(form, submitter) {
    setBusy(submitter, false);
    submitting = true;
    if (submitter && typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter);
    } else {
      form.submit();
    }
  }
})();
